import { useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getUrl } from '../../App';
import styles from './AnnotatedVideo.module.css'

const fetch_annotations = async (video_id) => {
  const response = await fetch(`${getUrl()}/api/video/annotation/${video_id}`)

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  return await response.json();
}

const get_color = (label) => {
  if (label === "Nao") {
    return "#dd1d1d"
  }
  if (label === "Person") {
    return "#fb7413"
  }
  if (label === "Ball") {
    return "#f278ad"
  }

}

const AnnotatedVideo = ({ video }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);

  const annotations = useQuery({
    queryKey: ['annotation,', video.id], queryFn: () => fetch_annotations(video.id),
    staleTime: 5 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    retry: (failureCount, error) => {
   
    if (error.response?.status === 400 || error.response?.status === 404) {
      return false;
    }
    return failureCount < 3;
  }
  })

  useEffect(() => {
    const videoEl = videoRef.current;
    const canvas = canvasRef.current;
    if (!videoEl || !canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;

    // Keep canvas internal resolution perfectly synced to video resolution
    const updateCanvasSize = () => {
      if (videoEl.videoWidth) {
        canvas.width = videoEl.videoWidth;
        canvas.height = videoEl.videoHeight;
        canvas.style.width = `${videoEl.clientWidth}px`;
        canvas.style.height = `${videoEl.clientHeight}px`;
      }
    };

    videoEl.addEventListener('loadedmetadata', updateCanvasSize);
    const resizeObserver = new ResizeObserver(() => updateCanvasSize());
    resizeObserver.observe(videoEl);

    const getCurrentBoxState = (sequence, currentTime, duration) => {
      if (!sequence || sequence.length === 0) return null;

      const startTime = sequence[0].time;
      const endTime = startTime + duration;

      // If video time is outside the lifespan of this annotation, hide it
      if (currentTime < startTime || currentTime > endTime) return null;

      // Find the keyframes right before and right after our current time
      let prev = sequence[0];
      let next = null;

      for (let i = 0; i < sequence.length; i++) {
        if (sequence[i].time <= currentTime) {
          prev = sequence[i];
        } else {
          next = sequence[i];
          break;
        }
      }

      if (!prev.enabled) return null;

      // If there is no next frame (we are past the last keyframe but within duration)
      if (!next) return prev;

      // Calculate the progress (0 to 1) between the previous and next keyframe
      const timeDiff = next.time - prev.time;
      const progress = (currentTime - prev.time) / timeDiff;

      return {
        x: prev.x + (next.x - prev.x) * progress,
        y: prev.y + (next.y - prev.y) * progress,
        width: prev.width + (next.width - prev.width) * progress,
        height: prev.height + (next.height - prev.height) * progress,
      };
    };

    

    const drawLoop = () => {
      const currentTime = videoEl.currentTime;

      // Clear previous frame
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const annotationResults = annotations.data?.result?.[4]?.[1]?.[0]?.result || [];
      annotationResults.forEach((annotation) => {
        const { sequence, duration, labels } = annotation.value;
        const box = getCurrentBoxState(sequence, currentTime, duration);

        if (box) {
          // Convert percentages from Labelstudio to video pixels
          const pxX = (box.x / 100) * canvas.width;
          const pxY = (box.y / 100) * canvas.height;
          const pxWidth = (box.width / 100) * canvas.width;
          const pxHeight = (box.height / 100) * canvas.height;

          let box_color = "#dd1d1d";
          // Draw Label
          if (labels && labels.length > 0) {

            const labelText = labels[0];
            box_color = get_color(labelText);
            const fontSize = Math.max(16, canvas.width / 70); // Scale font size
            ctx.font = `${fontSize}px Arial`;

            // Label Background
            const textWidth = ctx.measureText(labelText).width;
            ctx.fillStyle = box_color;
            ctx.fillRect(pxX, pxY - fontSize - 8, textWidth + 10, fontSize + 8);

            // Label Text
            ctx.fillStyle = '#000000';
            ctx.fillText(labelText, pxX + 5, pxY - 6);

          }

          ctx.strokeStyle = box_color;
          ctx.lineWidth = Math.max(2, canvas.width / 400);
          ctx.strokeRect(pxX, pxY, pxWidth, pxHeight);
        }
      });

      // Schedule next frame
      animationFrameId = requestAnimationFrame(drawLoop);
    };

  
    if (!annotations.isError && !annotations.isLoading && annotations.data) {
      drawLoop();
    }

    // Cleanup
   return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      videoEl.removeEventListener('loadedmetadata', updateCanvasSize);
      resizeObserver.disconnect();
    };
  }, [annotations.data, annotations.isError, annotations.isLoading]); 

  return (
    <div className={`${styles.video}`}>
      <video
        ref={videoRef}
        src={`https://logs.berlin-united.com/${video.video_path}`}
        controls
        muted
      />
      <canvas
        ref={canvasRef}
      />
    </div>
  );
};

export default AnnotatedVideo;